/**
 * The marketplace promotion a visitor arrived through — captured once, stored with a
 * fixed expiry, read by every checkout (C3.04, CR-B3b-1's write side).
 *
 * SIMPLER THAN `affiliateRef.js` ON PURPOSE: an affiliate referral is a server-signed
 * ticket whose window the SERVER judges when the sale happens, because it decides how long
 * ago a click may still earn a commission — inventing one client-side would attribute
 * money nobody agreed to pay. A promotion has no equivalent policy to invent: the server
 * revalidates "active, same item" at every checkout open
 * (`modules.crm.promotions.resolve_checkout_promotion`), so a stale stored id is
 * self-limiting regardless of how long this module keeps it — the fixed window below
 * exists only to stop `localStorage` from holding an old id indefinitely, not to gate
 * correctness.
 *
 * Every `localStorage` touch is guarded, same as `affiliateRef.js`: a bookkeeping
 * failure (Safari private mode) must never take a checkout down with it.
 */
import { PROMOTION_STORAGE_KEY, PROMOTION_QUERY_PARAM } from "../reviews/contract";

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 3;
// Loose on purpose: the id is a plain uuid4 hex (32 chars) today, but the ONLY
// consumer of shape here is "is this worth storing at all" — the server is the real
// validator, and a tighter pattern here would just be a second, driftable copy of it.
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const isValidPromotionId = (id) =>
  typeof id === "string" && ID_PATTERN.test(id);

const readRaw = () => {
  try {
    return window.localStorage.getItem(PROMOTION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeRaw = (value) => {
  try {
    window.localStorage.setItem(PROMOTION_STORAGE_KEY, value);
  } catch {
    /* Storage is unavailable. The attribution is lost; the sale is not. */
  }
};

export function clearPromotionRef() {
  try {
    window.localStorage.removeItem(PROMOTION_STORAGE_KEY);
  } catch {
    /* Same reasoning as writeRaw. */
  }
}

/** The `?pr=` a URL query carries, `""` when absent or malformed. */
export function promotionRefFromSearch(search) {
  let value;
  try {
    value = new URLSearchParams(search || "").get(PROMOTION_QUERY_PARAM);
  } catch {
    return "";
  }
  const id = (value || "").trim();
  return isValidPromotionId(id) ? id : "";
}

/**
 * Capture the promotion id this URL carries. Returns the id that was STORED, `""`
 * when nothing was — a caller uses the return value to decide whether to record a
 * click, mirroring `affiliateRef.captureFromLocation`.
 */
export function capturePromotionFromLocation(search) {
  const id = promotionRefFromSearch(search);
  if (!id) return "";
  writeRaw(JSON.stringify({ id, expiresAt: new Date(Date.now() + WINDOW_DAYS * DAY_MS).toISOString() }));
  return id;
}

/** The promotion id a checkout should send, `""` when there is none (absent or expired). */
export function currentPromotionRef() {
  const raw = readRaw();
  if (!raw) return "";
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPromotionRef();
    return "";
  }
  if (!parsed || !isValidPromotionId(parsed.id)) {
    clearPromotionRef();
    return "";
  }
  const expiresAt = Date.parse(parsed.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    clearPromotionRef();
    return "";
  }
  return parsed.id;
}

export default { capturePromotionFromLocation, currentPromotionRef, clearPromotionRef,
                 promotionRefFromSearch, isValidPromotionId };
